import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation';

const Container = styled.div`
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(20, 20, 30, 0.95);
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 36px;
  max-height: 100px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  font-size: 13px;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  resize: none;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: rgba(76, 175, 80, 0.5);
    background: rgba(255, 255, 255, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled.button`
  width: 36px;
  height: 36px;
  background: ${props => props.disabled
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(76, 175, 80, 0.3)'
  };
  border: 1px solid ${props => props.disabled
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(76, 175, 80, 0.5)'
  };
  border-radius: 8px;
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.3)' : 'white'};
  font-size: 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.4);
    transform: scale(1.02);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

/**
 * MessageInput - Text input for user messages
 *
 * Features:
 * - Auto-expanding textarea
 * - Enter to send, Shift+Enter for newline
 * - Send button with disabled state
 * - Auto-focus on mount
 */
const MessageInput = ({ onSendMessage, disabled }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const textAreaRef = useRef(null);

  // Auto-focus on mount
  useEffect(() => {
    if (textAreaRef.current && !disabled) {
      textAreaRef.current.focus();
    }
  }, [disabled]);

  // Auto-resize textarea
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (trimmed && !disabled) {
      onSendMessage(trimmed);
      setValue('');
    }
  };

  const handleKeyDown = (e) => {
    // Enter without Shift = send message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Container>
      <InputWrapper>
        <TextArea
          ref={textAreaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('chatAgent.inputPlaceholder')}
          disabled={disabled}
          rows={1}
        />
        <SendButton
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          aria-label={t('chatAgent.askMeAnything')}
        >
          ↑
        </SendButton>
      </InputWrapper>
    </Container>
  );
};

export default MessageInput;
